import { Component, ElementRef, HostListener, input, OnDestroy, output, signal, ViewChild } from '@angular/core';
import { Ticker } from '@shared/misc.utils';
import { add, clamp, isInside, lerp, magnitude, zero } from '@shared/vector.utils';
import { Vector2, Vector2CanvasNormalized } from '@typings/vectors';

@Component({
  selector: 'app-canvas-scroller',
  imports: [],
  templateUrl: './canvas-scroller.component.html',
  styleUrl: './canvas-scroller.component.css',
})
export class CanvasScrollerComponent implements OnDestroy {
  size = input.required<Vector2CanvasNormalized>();

  // pan
  easedOrigin = signal(zero());
  targetOrigin = signal(zero());
  panned = output<Vector2>();

  // dragging viewbox
  isDragging = signal(false);
  dragScreenOrigin = signal(zero());
  dragOrigin = signal(zero());
  dragStateChanged = output<'START' | 'STOP'>();

  @ViewChild('scroller', { read: ElementRef })
  scrollerEl!: ElementRef<HTMLElement>;

  private readonly ticker: Ticker;

  constructor() {
    this.ticker = new Ticker(() => {
      this.easedOrigin.set(lerp(this.easedOrigin(), this.targetOrigin(), 0.5));

      const el = this.scrollerEl?.nativeElement;
      if (!el) return;

      el.scrollLeft = this.easedOrigin()[0];
      el.scrollTop = this.easedOrigin()[1];
      this.panned.emit(this.easedOrigin());
    });
  }

  ngOnDestroy(): void {
    this.ticker.stop();
  }

  @HostListener('document:wheel', ['$event'])
  handleWheel(event: WheelEvent) {
    const deltaX = event.deltaX === 0 && event.shiftKey ? event.deltaY : event.deltaX;
    const deltaY = event.deltaX === 0 && event.shiftKey ? event.deltaX : event.deltaY;
    const delta = [deltaX, deltaY] as Vector2;

    const el = this.scrollerEl.nativeElement;
    const max = [el.scrollWidth - el.clientWidth, el.scrollHeight - el.clientHeight] as Vector2;
    this.targetOrigin.set(clamp(add(this.targetOrigin(), delta), zero(), max));
  }

  @HostListener('document:pointerdown', ['$event'])
  handlePointerDown(event: PointerEvent) {
    this.tryStartDrag(event.x, event.y);
  }

  @HostListener('document:pointermove', ['$event'])
  handlePointerMove(event: PointerEvent) {
    this.tryMoveDrag(event.x, event.y);
  }

  @HostListener('document:pointerup', ['$event'])
  handlePointerUp(event: PointerEvent) {
    this.tryEndDrag();
  }

  // Touch support
  @HostListener('document:touchstart', ['$event'])
  handleTouchStart(event: TouchEvent) {
    if (event.touches.length !== 1) return;
    const touch = event.touches[0];
    this.tryStartDrag(touch.clientX, touch.clientY);
  }

  @HostListener('document:touchmove', ['$event'])
  handleTouchMove(event: TouchEvent) {
    if (event.touches.length !== 1) return;
    const touch = event.touches[0];
    this.tryMoveDrag(touch.clientX, touch.clientY);
    event.preventDefault();
  }

  @HostListener('document:touchend', ['$event'])
  handleTouchEnd(event: TouchEvent) {
    this.tryEndDrag();
  }

  private tryStartDrag(x: number, y: number) {
    const el = this.scrollerEl.nativeElement;
    if (!el) return;
    const bounds = el.getBoundingClientRect();
    if (!isInside([x, y] as Vector2, [bounds.x, bounds.y] as Vector2, [bounds.width, bounds.height] as Vector2)) return;

    this.startDrag(x, y);
  }

  private startDrag(x: number, y: number) {
    this.dragScreenOrigin.set([x, y] as Vector2);
    this.dragOrigin.set([-this.easedOrigin()[0], -this.easedOrigin()[1]] as Vector2);
    this.isDragging.set(true);
  }

  private tryMoveDrag(x: number, y: number) {
    if (!this.isDragging()) return;

    this.moveDrag(x, y);
  }

  private moveDrag(x: number, y: number) {
    const position = [x, y] as Vector2;
    const deltaScreen = [position[0] - this.dragScreenOrigin()[0], position[1] - this.dragScreenOrigin()[1]] as Vector2;
    const distance = magnitude(deltaScreen);
    if (distance > 5) {
      this.dragStateChanged.emit('START');
    }
    const el = this.scrollerEl.nativeElement;
    const max = [el.scrollWidth - el.clientWidth, el.scrollHeight - el.clientHeight] as Vector2;
    this.targetOrigin.set(
      clamp([-(this.dragOrigin()[0] + deltaScreen[0]), -(this.dragOrigin()[1] + deltaScreen[1])] as Vector2, zero(), max),
    );
  }

  private tryEndDrag() {
    if (!this.isDragging()) return;

    this.endDrag();
  }

  private endDrag() {
    this.isDragging.set(false);
    this.dragStateChanged.emit('STOP');
  }
}
